// SAB-002 — canonical legal-authority identity.
//
// The `legal_research` table can hold multiple rows for the SAME authority
// (re-runs of the research stage, or the same statute retrieved under two
// topics). Without one shared identity rule, each surface counted the same
// statute a different number of times. This module owns that rule; every
// consumer must dedupe through `dedupeLegalAuthorities` before counting or
// rendering legal authorities.

export interface LegalAuthorityLike {
  citation?: string | null;
  statute?: string | null;
  title?: string | null;
  confidence?: number | null;
  source_url?: string | null;
  verification_status?: string | null;
}

/**
 * Identity key for an authority: the citation (or statute, or title) with
 * punctuation, section glyphs and casing normalized away, so
 * "Tex. Occ. Code § 2308.208" and "TEX OCC CODE 2308.208" collapse to one.
 */
export function normalizeAuthorityKey(row: LegalAuthorityLike): string {
  const raw = (row.citation ?? row.statute ?? row.title ?? "").toString();
  return raw
    .toLowerCase()
    .replace(/§/g, " ")
    // Abbreviation periods are cosmetic ("Tex. Occ. Code" vs "TEX OCC CODE");
    // periods inside section numbers (2308.208) are significant.
    .replace(/\.(?!\d)/g, " ")
    .replace(/[^a-z0-9.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Collapse duplicate authorities, preserving first-seen order. When two rows
 * share an identity, the one carrying the most evidence wins (verified over
 * unverified, then a source URL, then higher confidence) so dedupe never
 * discards the better-supported record.
 */
export function dedupeLegalAuthorities<T extends LegalAuthorityLike>(
  rows: readonly T[] | null | undefined,
): T[] {
  const order: string[] = [];
  const best = new Map<string, T>();
  for (const row of rows ?? []) {
    const key = normalizeAuthorityKey(row);
    if (!key) continue;
    const current = best.get(key);
    if (!current) {
      order.push(key);
      best.set(key, row);
      continue;
    }
    if (score(row) > score(current)) best.set(key, row);
  }
  return order.map((k) => best.get(k)!);
}

function score(row: LegalAuthorityLike): number {
  const verified =
    String((row as { verification_status?: unknown }).verification_status ?? "").toLowerCase() ===
    "verified";
  const confidence =
    typeof row.confidence === "number" && Number.isFinite(row.confidence) ? row.confidence : 0;
  return (verified ? 100 : 0) + (row.source_url ? 10 : 0) + confidence;
}
